import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const REMEMBERED_KEY = "scanmint_remembered_email";
  const initialEmail = (() => {
    try {
      return localStorage.getItem(REMEMBERED_KEY) || "";
    } catch {
      return "";
    }
  })();
  const [form, setForm] = useState({ email: initialEmail, password: "" });
  const [remember, setRemember] = useState(!!initialEmail);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      try {
        if (remember) localStorage.setItem(REMEMBERED_KEY, form.email);
        else localStorage.removeItem(REMEMBERED_KEY);
      } catch {}
      toast.success(`Welcome back, ${user.name}`);
      if (user.role === "super_admin") nav("/admin");
      else nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#09090B] text-white relative overflow-hidden">
        <div className="relative z-10">
          <Link
            to="/"
            className="inline-block bg-white rounded-xl p-3 shadow-sm"
            data-testid="login-brand"
          >
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto block"
              draggable={false}
            />
          </Link>
        </div>
        <div className="relative z-10">
          <div className="mono-tiny text-white/50 mb-3">Verify · Manage · Scale</div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Welcome back to the verification layer for real-world assets.
          </h2>
          <p className="mt-4 text-white/60 max-w-sm">
            Log in to manage your tenants, vehicles, QR stickers, and scan history.
          </p>
        </div>
        <div className="mono-tiny text-white/40 relative z-10">© 2026 ScanMint</div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#0F8F4F_0%,transparent_60%)]" />
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="md:hidden mb-4">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Sign in</h1>
            <p className="text-sm text-[#71717A] mt-2">Use your ScanMint account to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="mono-tiny text-[#71717A]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-12"
                data-testid="login-email"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="mono-tiny text-[#71717A]">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#0F8F4F] font-medium hover:underline"
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-12 pr-12"
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#09090B] transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-[#0F8F4F]/40"
                  data-testid="login-toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" strokeWidth={1.75} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-[#3F3F46]">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-[#E4E4E7] text-[#0F8F4F] focus:ring-[#0F8F4F]"
              data-testid="login-remember-me"
            />
            <span>Remember me on this device</span>
          </label>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-base"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign in"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>

          <div className="text-sm text-[#71717A]">
            No account yet?{" "}
            <Link to="/register" className="text-[#0F8F4F] font-medium" data-testid="login-to-register">
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
